import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, ConfigProvider, Form, Input, Modal, Select, Spin, Table } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import useAxiosClient from "../axios-client.js";
import { useStateContext } from "../contexts/ContextProvider.jsx";
import { shellAntdTheme, STATUS } from "../shellTokens";
import "../components/Settings/RoleSettings/access.css";

/**
 * Settings → Access, Permissions tab.
 *
 * Two jobs in one master/detail: the catalogue on the left is the vocabulary,
 * and the pane on the right answers "who can do this thing" — the reverse of
 * the role editor, and the view that was missing entirely.
 *
 * The pane is read-only by design: changing what a role grants from here would
 * change it for everyone in that role, so every row is a link out to the place
 * the change is actually made. Only the permission's own labels are editable.
 */

const UNGROUPED = 'Ungrouped';

const PermissionSettings = () => {
  const axiosClient = useAxiosClient();
  const navigate = useNavigate();
  const { darkMode, permissions: myPermissions } = useStateContext();
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [holders, setHolders] = useState(null);
  const [holdersLoading, setHoldersLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const searchRef = useRef(null);

  const canAuthor = (myPermissions || []).includes('PERMISSION_SETTINGS');
  const canSeeRoles = (myPermissions || []).includes('ROLE_SETTINGS');

  const getPermissions = useCallback(async (keepSelection) => {
    setLoading(true);
    try {
      const response = await axiosClient.get(`/permissions/getPermissions`);
      setPermissions(response.data);
      setSelected((current) => {
        const stillThere = keepSelection && response.data.find((p) => p.id === current?.id);
        return stillThere || response.data[0] || null;
      });
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [axiosClient]);

  useEffect(() => { getPermissions(false); }, [getPermissions]);

  useEffect(() => {
    if (!selected) { setHolders(null); return undefined; }
    let cancelled = false;

    setHoldersLoading(true);
    axiosClient.get(`/permissions/${selected.id}/holders`)
      .then((r) => { if (!cancelled) setHolders(r.data); })
      .catch((error) => {
        if (cancelled) return;
        const message = (error.response && error.response.data && error.response.data.message)
          || error.message || error.toString();
        toast.error(message);
      })
      .finally(() => { if (!cancelled) setHoldersLoading(false); });

    return () => { cancelled = true; };
  }, [selected, axiosClient]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      searchRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    const byGroup = new Map();

    permissions.forEach((p) => {
      const hit = !term
        || p.name.toLowerCase().includes(term)
        || (p.description || '').toLowerCase().includes(term)
        || (p.group || '').toLowerCase().includes(term);
      if (!hit) return;
      const key = p.group || UNGROUPED;
      if (!byGroup.has(key)) byGroup.set(key, []);
      byGroup.get(key).push(p);
    });

    return [...byGroup.entries()].map(([name, rows]) => ({ name, rows }));
  }, [permissions, search]);

  const groupOptions = useMemo(
    () => [...new Set(permissions.map((p) => p.group).filter(Boolean))].map((g) => ({ value: g, label: g })),
    [permissions],
  );

  const startEdit = () => {
    editForm.setFieldsValue({ description: selected.description, group: selected.group });
    setEditing(true);
  };

  const saveEdit = async (values) => {
    setSaving(true);
    try {
      const response = await axiosClient.put(`/permissions/${selected.id}`, values);
      toast.success(response.data.message);
      setEditing(false);
      setSelected(response.data.permission);
      setPermissions((prev) => prev.map((p) => (p.id === selected.id ? response.data.permission : p)));
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const createPermission = async (data) => {
    setSaving(true);
    try {
      const response = await axiosClient.post(`/permissions/createPermission`, data);
      toast.success(response.data.message);
      setCreateOpen(false);
      createForm.resetFields();
      getPermissions(true);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      // force: the typed-name confirmation *is* the acknowledgement the API
      // asks for when the permission is still held by anyone.
      const response = await axiosClient.delete(`/permissions/delete/${selected.id}?force=1`);
      toast.success(response.data.message);
      setDeleteOpen(false);
      setConfirmText('');
      setSelected(null);
      getPermissions(false);
    } catch (error) {
      const message = (error.response && error.response.data && error.response.data.message)
        || error.message || error.toString();
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const userColumns = [
    {
      title: 'User',
      dataIndex: 'name',
      render: (name, record) => <Link to={`/users/${record.id}`}>{name}</Link>,
    },
    {
      title: 'Via',
      key: 'via',
      render: (_, record) => (
        <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>
          {record.via.map((role) => (
            <span key={role} className="et-access__chip et-access__chip--via">{role}</span>
          ))}
          {record.direct && <span className="et-access__chip et-access__chip--direct">DIRECT</span>}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      width: 110,
      // The next thing you want after "six people hold DELETE_ORDER" is to
      // remove one, so the fix is one click away rather than a name to remember.
      render: (_, record) => <Link to={`/users/${record.id}`}>Access →</Link>,
    },
  ];

  return (
    <ConfigProvider theme={shellAntdTheme(darkMode)}>
      <div className="et-access animate-fade-in">
        <div style={{ marginBottom: 14 }}>
          <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--dim)' }}>Settings</div>
          <h1 style={{ font: '700 22px Outfit, sans-serif', letterSpacing: '-.01em', margin: '1px 0 0' }}>
            Access
          </h1>
        </div>

        <div className="et-access__tabs" style={{ marginBottom: 16 }}>
          {canSeeRoles && (
            <button type="button" className="et-access__tab" onClick={() => navigate('/settings/roleSettings')}>
              Roles
            </button>
          )}
          <button type="button" className="et-access__tab et-access__tab--on">Permissions</button>
        </div>

        <div className="et-access__cols et-access__cols--catalogue">
          {/* ---- Catalogue ---- */}
          <div className="et-access__card">
            <div className="et-access__cardhead">
              <div>
                <div style={{ font: '700 14.5px Outfit, sans-serif' }}>Catalogue</div>
                <div style={{ font: '400 12px Outfit, sans-serif', color: 'var(--muted)', marginTop: 2 }}>
                  {`${permissions.length} permissions in ${new Set(permissions.map((p) => p.group)).size} groups`}
                </div>
              </div>
              {canAuthor && <Button size="small" onClick={() => setCreateOpen(true)}>+ New</Button>}
            </div>

            <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--border)' }}>
              <Input
                ref={searchRef}
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search permissions"
                prefix={<SearchOutlined style={{ color: 'var(--dim)' }} />}
              />
            </div>

            <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: '60vh', overflowY: 'auto' }}>
              {loading && !permissions.length && <div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>}
              {groups.map((group) => (
                <div key={group.name}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '10px 8px 6px',
                  }}>
                    <span style={{ font: '700 12px Outfit, sans-serif' }}>{group.name}</span>
                    <span className="et-access__meta">{group.rows.length}</span>
                  </div>
                  {group.rows.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={`et-access__catrow${selected?.id === p.id ? ' et-access__catrow--on' : ''}`}
                      onClick={() => { setEditing(false); setSelected(p); }}
                    >
                      <span className="et-access__name" style={{ fontSize: 12 }}>{p.name}</span>
                      {p.deprecated && <span className="et-access__chip et-access__chip--deprecated">DEPRECATED</span>}
                    </button>
                  ))}
                </div>
              ))}
              {!loading && groups.length === 0 && (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--dim)', fontSize: 12.5 }}>
                  {`Nothing matches "${search.trim()}".`}
                </div>
              )}
            </div>
          </div>

          {/* ---- Detail ---- */}
          <div className="et-access__card">
            {!selected ? (
              <div style={{ padding: 60, textAlign: 'center', color: 'var(--dim)' }}>
                Select a permission to see who holds it.
              </div>
            ) : (
              <>
                <div className="et-access__cardhead">
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span className="et-access__name" style={{ fontSize: 15 }}>{selected.name}</span>
                      <span className="et-access__chip et-access__chip--deprecated">
                        {(selected.group || UNGROUPED).toUpperCase()}
                      </span>
                      {selected.deprecated && (
                        <span className="et-access__chip et-access__chip--locked">DEPRECATED</span>
                      )}
                    </div>
                    <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--muted)', marginTop: 4 }}>
                      {selected.description || 'No description.'}
                      {selected.deprecated && selected.replacedBy && ` Superseded by ${selected.replacedBy}.`}
                    </div>
                  </div>
                  {canAuthor && !editing && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button size="small" onClick={startEdit}>Edit</Button>
                      <Button size="small" danger onClick={() => { setConfirmText(''); setDeleteOpen(true); }}>
                        Delete
                      </Button>
                    </div>
                  )}
                </div>

                {editing && (
                  <div style={{ padding: 18, borderBottom: '1px solid var(--border)' }}>
                    <Form form={editForm} layout="vertical" onFinish={saveEdit}>
                      <Form.Item name="description" label="Description">
                        <Input.TextArea rows={2} placeholder="What holding this permission lets someone do." />
                      </Form.Item>
                      <Form.Item name="group" label="Group">
                        <Select allowClear showSearch options={groupOptions} placeholder="Ungrouped" />
                      </Form.Item>
                      <div style={{
                        font: '400 11.5px/1.6 Outfit, sans-serif',
                        color: 'var(--dim)',
                        marginBottom: 14,
                      }}>
                        The name is permanent — it is the string the API and the middleware match on.
                        Description and group are labels and can change freely.
                      </div>
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <Button onClick={() => setEditing(false)}>Cancel</Button>
                        <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
                      </div>
                    </Form>
                  </div>
                )}

                {holdersLoading || !holders ? (
                  <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
                ) : (
                  <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 20 }}>
                    <div>
                      <div className="et-access__sectionlabel">
                        Held by roles
                        <span className="et-access__meta">{holders.roles.length}</span>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {holders.roles.length === 0 && (
                          <div style={{ font: '400 12.5px Outfit, sans-serif', color: 'var(--dim)' }}>
                            No role grants this.
                          </div>
                        )}
                        {holders.roles.map((role) => (
                          <Link key={role.id} to="/settings/roleSettings" className="et-access__holderrow">
                            <span style={{ font: '600 13px Outfit, sans-serif' }}>{role.name}</span>
                            {role.protected && (
                              <span className="et-access__chip et-access__chip--protected">PROTECTED</span>
                            )}
                            <span className="et-access__meta" style={{ marginLeft: 'auto' }}>
                              {`${role.usersCount} user${role.usersCount === 1 ? '' : 's'} →`}
                            </span>
                          </Link>
                        ))}
                      </div>
                      <div style={{
                        font: '400 11.5px/1.6 Outfit, sans-serif',
                        color: 'var(--dim)',
                        marginTop: 8,
                      }}>
                        Changing a role here would change it for everyone in it, so this pane only
                        points at the role editor.
                      </div>
                    </div>

                    <div>
                      <div className="et-access__sectionlabel">
                        Held by users
                        <span className="et-access__meta">{holders.users.length}</span>
                        {holders.directCount > 0 && (
                          <span style={{ font: '400 11.5px Outfit, sans-serif', color: STATUS.warn }}>
                            {`${holders.directCount} holds it directly`}
                          </span>
                        )}
                      </div>
                      <Table
                        rowKey="id"
                        size="small"
                        columns={userColumns}
                        dataSource={holders.users}
                        pagination={holders.users.length > 10 ? { pageSize: 10, size: 'small' } : false}
                        locale={{ emptyText: 'Nobody holds this permission.' }}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* ---- Create ---- */}
        <Modal
          title="Add a permission"
          open={createOpen}
          onCancel={() => { createForm.resetFields(); setCreateOpen(false); }}
          footer={null}
        >
          <Form form={createForm} layout="vertical" onFinish={createPermission} style={{ marginTop: 16 }}>
            <Form.Item
              name="name"
              label="Name"
              rules={[
                { required: true, message: 'Please input a permission name!' },
                {
                  pattern: /^[A-Z][A-Z0-9_]*$/,
                  message: 'Use SCREAMING_SNAKE_CASE — this string is matched by the middleware.',
                },
              ]}
            >
              <Input placeholder="MANAGE_SOMETHING" className="et-access__name" />
            </Form.Item>
            <Form.Item name="description" label="Description" rules={[{ required: true, message: 'Describe what it allows!' }]}>
              <Input.TextArea rows={2} placeholder="What holding this permission lets someone do." />
            </Form.Item>
            <Form.Item name="group" label="Group">
              <Select allowClear showSearch options={groupOptions} placeholder="Ungrouped" />
            </Form.Item>
            <div style={{ font: '400 11.5px/1.6 Outfit, sans-serif', color: 'var(--dim)', marginBottom: 14 }}>
              The name cannot be changed later. A new permission is granted to SUDO on creation so
              it stays reachable; nothing else holds it until you grant it.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button onClick={() => { createForm.resetFields(); setCreateOpen(false); }}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving}>Create</Button>
            </div>
          </Form>
        </Modal>

        {/* ---- Delete, with blast radius ---- */}
        <Modal
          title={selected ? `Delete ${selected.name}?` : 'Delete permission'}
          open={deleteOpen}
          onCancel={() => { setDeleteOpen(false); setConfirmText(''); }}
          footer={null}
          destroyOnClose
        >
          {selected && holders && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 8 }}>
              <div style={{ font: '400 13px/1.6 Outfit, sans-serif', color: 'var(--muted)' }}>
                The permission is removed from every role and user that holds it. Anything checking
                it server-side starts failing immediately. This cannot be undone.
              </div>

              <div style={{
                padding: '12px 14px',
                borderRadius: 10,
                background: 'rgba(248,113,113,.08)',
                border: '1px solid rgba(248,113,113,.28)',
              }}>
                <div className="et-access__sectionlabel" style={{ marginBottom: 6 }}>Who loses access</div>
                <div style={{ font: '400 12.5px/1.7 Outfit, sans-serif', color: 'var(--muted)' }}>
                  <strong style={{ color: 'var(--text)' }}>{holders.roles.length}</strong>
                  {` role${holders.roles.length === 1 ? '' : 's'}`}
                  {holders.roles.length > 0 && ` — ${holders.roles.map((r) => r.name).join(', ')}`}
                  <br />
                  <strong style={{ color: 'var(--text)' }}>{holders.users.length}</strong>
                  {` user${holders.users.length === 1 ? '' : 's'}`}
                  {holders.directCount > 0 && `, ${holders.directCount} of them by direct grant`}
                </div>
              </div>

              <div>
                <div style={{ font: '600 12px Outfit, sans-serif', color: 'var(--muted)', marginBottom: 6 }}>
                  Type the name to confirm.
                </div>
                <Input
                  className="et-access__name"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={selected.name}
                  autoFocus
                />
              </div>

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button onClick={() => { setDeleteOpen(false); setConfirmText(''); }}>Cancel</Button>
                <Button
                  danger
                  type="primary"
                  loading={deleting}
                  disabled={confirmText !== selected.name}
                  onClick={confirmDelete}
                >
                  Delete permission
                </Button>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </ConfigProvider>
  );
};

export default PermissionSettings;
